import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import { CheckCircle2, Send } from "lucide-react";

const inputCls = "border border-slate-200 rounded-lg px-3 py-2.5 text-sm w-full focus:border-[#0EA5E9] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/20 bg-white";

const STATUS_BADGE = {
  DRAFT: "bg-slate-100 text-[#475569]",
  PENDING_APPROVAL: "bg-[#F59E0B]/10 text-[#F59E0B]",
  ACTIVE: "bg-[#10B981]/10 text-[#10B981]",
  PAUSED: "bg-slate-100 text-[#94A3B8]",
  SUSPENDED: "bg-[#EF4444]/10 text-[#EF4444]",
  REJECTED: "bg-[#EF4444]/10 text-[#EF4444]",
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-bold text-[#0B1B3D] font-display">{label}</label>
    {children}
  </div>
);

export default function BecomeExpert() {
  const { t } = useT();
  const [tax, setTax] = useState(null);
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", country: "", city: "", headline: "", bio: "",
    years_experience: 5, current_role: "", current_company: "",
    primary_expertise: [], secondary_expertise: [], industries: [],
    languages: [{ language: "English", level: "Professional" }],
    availability: "AVAILABLE", turnaround: "48H",
    service: { active: true, description: "", base_price_eur: 150, turnaround: "48H" },
  });

  useEffect(() => {
    (async () => {
      const [tx, me] = await Promise.all([api.get("/consultants/taxonomies"), api.get("/consultants/me")]);
      setTax(tx.data);
      if (me.data.profile) {
        setProfile(me.data.profile);
        const p = me.data.profile;
        setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(p).filter(([k]) => k in f)) }));
      }
    })().catch(() => toast.error("Could not load"));
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v, max) => setForm((f) => {
    const arr = f[k].includes(v) ? f[k].filter((x) => x !== v) : (f[k].length < max ? [...f[k], v] : f[k]);
    if (!f[k].includes(v) && f[k].length >= max) toast.error(`${t("Maximum")} ${max}`);
    return { ...f, [k]: arr };
  });

  const save = async (thenSubmit = false) => {
    setSaving(true);
    try {
      const r = await api.post("/consultants/profile", { ...form, years_experience: Number(form.years_experience), service: { ...form.service, base_price_eur: Number(form.service.base_price_eur) } });
      setProfile(r.data.profile);
      if (thenSubmit) {
        const s = await api.post("/consultants/profile/submit", { accept_terms: acceptTerms });
        setProfile(s.data.profile);
        toast.success(t("Profile submitted for approval"));
      } else {
        toast.success(t("Profile saved"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Save failed"));
    } finally { setSaving(false); }
  };

  if (!tax) return <DashboardLayout title={t("Become a Konzenzo Expert")}><div className="text-[#94A3B8]">{t("Loading…")}</div></DashboardLayout>;

  const status = profile?.consultant_status;
  const editable = !status || status === "DRAFT" || status === "REJECTED" || status === "ACTIVE" || status === "PAUSED";

  return (
    <DashboardLayout title={t("Become a Konzenzo Expert")}
      action={status && <span data-testid="consultant-status-badge" className={`px-3 py-1 rounded-full text-xs font-bold font-mono-kz uppercase tracking-wider ${STATUS_BADGE[status]}`}>{t(status)}</span>}>
      <div className="max-w-3xl space-y-6">
        <p className="text-[#475569]">{t("Offer independent professional reviews of Konzenzo Decision Reports and get paid for your expertise. The AI analyzes the decision first — you validate it.")}</p>

        {status === "PENDING_APPROVAL" && (
          <div data-testid="pending-banner" className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl p-4 text-sm text-[#0B1B3D]">
            {t("Your profile is under review by the Konzenzo team. We will notify you once approved.")}
          </div>
        )}
        {status === "ACTIVE" && (
          <div data-testid="active-banner" className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-4 text-sm text-[#0B1B3D] flex items-center justify-between gap-2 flex-wrap">
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> {t("Your profile is live in the Marketplace.")}</span>
            <a href="/expert-dashboard" className="font-bold text-[#0EA5E9]">{t("Go to Expert dashboard")} →</a>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl p-8 grid md:grid-cols-2 gap-5">
          <Field label={t("First name")}><input data-testid="expert-first-name" className={inputCls} value={form.first_name} onChange={(e) => upd("first_name", e.target.value)} disabled={!editable} /></Field>
          <Field label={t("Last name")}><input data-testid="expert-last-name" className={inputCls} value={form.last_name} onChange={(e) => upd("last_name", e.target.value)} disabled={!editable} /></Field>
          <Field label={t("Country")}><input data-testid="expert-country" className={inputCls} value={form.country} onChange={(e) => upd("country", e.target.value)} placeholder="Germany" disabled={!editable} /></Field>
          <Field label={t("City (optional)")}><input className={inputCls} value={form.city || ""} onChange={(e) => upd("city", e.target.value)} disabled={!editable} /></Field>
          <div className="md:col-span-2"><Field label={t("Professional headline")}><input data-testid="expert-headline" className={inputCls} value={form.headline} onChange={(e) => upd("headline", e.target.value)} placeholder={t("e.g., CFO advisor for manufacturing SMEs")} disabled={!editable} /></Field></div>
          <div className="md:col-span-2"><Field label={t("Professional bio")}><textarea data-testid="expert-bio" rows={4} className={inputCls} value={form.bio} onChange={(e) => upd("bio", e.target.value)} disabled={!editable} /></Field></div>
          <Field label={t("Years of experience")}><input type="number" min="0" max="60" className={inputCls} value={form.years_experience} onChange={(e) => upd("years_experience", e.target.value)} disabled={!editable} /></Field>
          <Field label={t("Current role (optional)")}><input className={inputCls} value={form.current_role || ""} onChange={(e) => upd("current_role", e.target.value)} disabled={!editable} /></Field>

          <div className="md:col-span-2"><Field label={`${t("Primary expertise")} (max 3)`}>
            <div className="space-y-3">
              {Object.entries(tax.expertise_taxonomy).map(([group, items]) => (
                <div key={group}>
                  <p className="font-mono-kz text-xs text-[#94A3B8] uppercase tracking-widest font-bold mb-1.5">{group}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {items.map((e) => {
                      const sel = form.primary_expertise.includes(e);
                      const sec = form.secondary_expertise.includes(e);
                      return (
                        <button key={e} data-testid={`expertise-${e}`} type="button" disabled={!editable}
                          onClick={() => sel ? toggle("primary_expertise", e, 3) : sec ? toggle("secondary_expertise", e, 10) : (form.primary_expertise.length < 3 ? toggle("primary_expertise", e, 3) : toggle("secondary_expertise", e, 10))}
                          className={`px-2.5 py-1 rounded-full text-xs font-bold border transition-colors ${sel ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : sec ? "bg-[#0EA5E9]/10 text-[#0EA5E9] border-[#0EA5E9]/30" : "bg-white text-[#475569] border-slate-200 hover:border-[#0EA5E9]"}`}>
                          {tax.expertise_labels[e] || e}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <p className="text-xs text-[#94A3B8] font-mono-kz">{t("Dark = primary (max 3), light blue = secondary (max 10). Click to cycle.")}</p>
            </div>
          </Field></div>

          <div className="md:col-span-2"><Field label={t("Industries")}>
            <div className="flex flex-wrap gap-1.5">
              {tax.industries.map((i) => (
                <button key={i} data-testid={`industry-${i.replace(/\s+/g, "-")}`} type="button" disabled={!editable} onClick={() => toggle("industries", i, 12)}
                  className={`px-2.5 py-1 rounded-full text-xs font-bold border ${form.industries.includes(i) ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : "bg-white text-[#475569] border-slate-200 hover:border-[#0EA5E9]"}`}>{i}</button>
              ))}
            </div>
          </Field></div>

          <div className="md:col-span-2"><Field label={t("Languages")}>
            <div className="space-y-2">
              {form.languages.map((l, i) => (
                <div key={i} className="flex gap-2">
                  <input className={inputCls} value={l.language} placeholder="English" disabled={!editable}
                    onChange={(e) => upd("languages", form.languages.map((x, j) => j === i ? { ...x, language: e.target.value } : x))} />
                  <select className={inputCls} value={l.level} disabled={!editable}
                    onChange={(e) => upd("languages", form.languages.map((x, j) => j === i ? { ...x, level: e.target.value } : x))}>
                    {tax.language_levels.map((lv) => <option key={lv}>{lv}</option>)}
                  </select>
                  <button type="button" disabled={!editable} onClick={() => upd("languages", form.languages.filter((_, j) => j !== i))} className="text-[#EF4444] px-2">✕</button>
                </div>
              ))}
              {editable && <button type="button" data-testid="add-language-btn" onClick={() => upd("languages", [...form.languages, { language: "", level: "Professional" }])} className="text-sm font-bold text-[#0EA5E9]">+ {t("Add language")}</button>}
            </div>
          </Field></div>

          <Field label={t("Availability")}>
            <select data-testid="expert-availability" className={inputCls} value={form.availability} onChange={(e) => upd("availability", e.target.value)} disabled={!editable}>
              {tax.availability_options.map((a) => <option key={a} value={a}>{t(a)}</option>)}
            </select>
          </Field>
          <Field label={t("Turnaround")}>
            <select data-testid="expert-turnaround" className={inputCls} value={form.turnaround} onChange={(e) => { upd("turnaround", e.target.value); setForm((f) => ({ ...f, service: { ...f.service, turnaround: e.target.value } })); }} disabled={!editable}>
              {tax.turnaround_options.map((a) => <option key={a}>{a}</option>)}
            </select>
          </Field>

          <div className="md:col-span-2 border-t border-slate-100 pt-5">
            <p className="font-display font-extrabold text-[#0B1B3D] mb-3">{t("Report Review service")}</p>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={`${t("Price per review")} (EUR)`}><input data-testid="service-price" type="number" min="1" className={inputCls} value={form.service.base_price_eur} onChange={(e) => setForm((f) => ({ ...f, service: { ...f.service, base_price_eur: e.target.value } }))} disabled={!editable} /></Field>
              <div className="md:col-span-2"><Field label={t("Service description")}><textarea rows={2} className={inputCls} value={form.service.description} onChange={(e) => setForm((f) => ({ ...f, service: { ...f.service, description: e.target.value } }))} disabled={!editable} /></Field></div>
            </div>
          </div>

          {(!status || status === "DRAFT" || status === "REJECTED") && (
            <div className="md:col-span-2 flex items-start gap-2 bg-[#F8FAFC] border border-slate-200 rounded-lg p-4">
              <input data-testid="accept-terms-checkbox" type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="mt-1" />
              <p className="text-sm text-[#475569]">{t("I accept the Konzenzo Marketplace Terms: independent professional relationship, confidentiality of shared reports, marketplace commission on paid reviews, and professional responsibility for my opinions.")}</p>
            </div>
          )}

          <div className="md:col-span-2 flex justify-end gap-3">
            {editable && <button data-testid="save-profile-btn" disabled={saving} onClick={() => save(false)} className="border border-slate-200 text-[#0B1B3D] font-bold px-5 py-2.5 rounded-lg text-sm font-display hover:border-[#0EA5E9]">{saving ? t("Saving…") : t("Save draft")}</button>}
            {(!status || status === "DRAFT" || status === "REJECTED") && (
              <button data-testid="submit-profile-btn" disabled={saving || !acceptTerms} onClick={() => save(true)} className="bg-[#10B981] disabled:opacity-40 text-white hover:bg-[#059669] px-5 py-2.5 rounded-lg font-bold text-sm font-display inline-flex items-center gap-2">
                <Send className="w-4 h-4" /> {t("Submit for approval")}
              </button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
